import { useEffect, useRef, useState } from "react";
import type { ExtractedSourceDocument, OrganisationAllowance } from "@keydials/api-client";
import { segmentNoun, setKind, type SegmentNoun } from "@keydials/dial-workspace";
import { useAuthoring } from "../AuthoringProvider";
import { startBlank, startFromTemplate, type CreateResult, type LoadedDraft } from "../builderLoad";
import { builderCopy as copy, fill, nounValues } from "../copy";
import { aiMeter, describeCard, documentProblem, readDocument, startFromDescription, type DescribeResult, type DocumentResult } from "../describe";
import type { DraftSession, DraftSnapshot, NounForms } from "../draftSession";
import { switchToSimpleQuestion } from "../kindSwitch";
import { LimitSheet } from "../LimitSheet";
import { designLimitSheet } from "../limitSheetView";
import { suggestPlural } from "../nounForms";
import { PlanMeter } from "../PlanMeter";
import { meterView } from "../planMeterView";
import { TemplatePicker } from "../TemplatePicker";
import { builderDeps, describeDeps } from "../useDraftSession";
import type { StartChoice } from "../stages";
import { DescribeStart } from "./DescribeStart";

type Kind = "Simple" | "Itemised";

const nounFor = (forms: NounForms | null): SegmentNoun =>
  segmentNoun(forms ? { segmentNounSingular: forms.singular, segmentNounPlural: forms.plural } : null);

/**
 * Start (KEY-560): what the dial is called and for, how to begin, which kind it is, and what it calls an action point. A new dial
 * checks the plan before anything is created; a dial already made edits the same answers in place, autosaved.
 */
export function StartStage(
  props:
    | { mode: "new"; initialStart?: StartChoice | null; onCreated: (draft: LoadedDraft) => void; onLeave: () => void }
    | { mode: "editing"; session: DraftSession; snapshot: DraftSnapshot; onNext: () => void },
) {
  return props.mode === "new" ? <NewDial {...props} /> : <EditDial {...props} />;
}

function NewDial({
  initialStart,
  onCreated,
  onLeave,
}: {
  initialStart?: StartChoice | null;
  onCreated: (draft: LoadedDraft) => void;
  onLeave: () => void;
}) {
  const [allowance, setAllowance] = useState<OrganisationAllowance | null>(null);
  const [limit, setLimit] = useState<OrganisationAllowance | null>(null);
  // A way in on My Dials can have chosen already; the author can still pick another.
  const [start, setStart] = useState<StartChoice | null>(initialStart ?? null);
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [kind, setKindChoice] = useState<Kind>("Simple");
  const [singular, setSingular] = useState("");
  const [plural, setPlural] = useState("");
  const [pluralTyped, setPluralTyped] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const drafts = useRef<AbortController | null>(null);
  // A document for Describe it, offered only where the server can read one.
  const documentsOffered = useAuthoring().status?.documentSeed === true;
  const [attached, setAttached] = useState<ExtractedSourceDocument | null>(null);
  const [reading, setReading] = useState<string | null>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [documentsUnavailable, setDocumentsUnavailable] = useState(false);
  // Bumped by every read and by leaving Start, so a read that finishes late never lands.
  const documentReads = useRef(0);

  const refreshAllowance = () =>
    builderDeps()
      .getAllowance()
      .then((answer) => {
        setAllowance(answer);
        return answer;
      });

  // Fail early: a full plan opens the limit sheet before anyone types, not after they press Continue.
  useEffect(() => {
    let cancelled = false;
    builderDeps()
      .getAllowance()
      .then((answer) => {
        if (cancelled) return;
        setAllowance(answer);
        if (!answer.canCreateDefinition) setLimit(answer);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      // Leaving Start stops waiting for a draft. The draft itself can't be called back, but nothing is created from it.
      drafts.current?.abort();
      documentReads.current += 1;
    };
  }, []);

  const noun: NounForms | null = singular.trim() && plural.trim() ? { singular: singular.trim(), plural: plural.trim() } : null;
  const words = nounFor(noun);

  function settle(result: CreateResult | DescribeResult) {
    if (result.kind === "created") onCreated(result.draft);
    else if (result.kind === "limit") setLimit(result.allowance);
    else if (result.kind === "refused") setError(result.message || copy.frame.notSaved);
    else if (result.kind === "aiRefused") setError(result.message);
    else if (result.kind === "tooLong") setError(copy.start.describe.tooLong);
    else if (result.kind === "aiUnavailable") setError(copy.start.describe.aiUnavailable);
    else if (result.kind === "failed") setError(copy.start.describe.failed);
    // "trialEnded": the 402 behind it has already switched the app to the trial-ended screen (KEY-637).
  }

  async function readFile(file: File) {
    const read = ++documentReads.current;
    setReading(file.name);
    setDocumentError(null);
    let result: DocumentResult;
    try {
      result = await readDocument(describeDeps(), file);
    } catch {
      result = { kind: "failed" };
    }
    if (documentReads.current !== read) return;
    setReading(null);
    if (result.kind === "attached") {
      setAttached(result.document);
      return;
    }
    if (result.kind === "trialEnded") {
      // The 402 behind it has already switched the app to the trial-ended screen (KEY-637).
      return;
    }
    if (result.kind === "unavailable") {
      // Documents are off here after all: the draft couldn't use the one attached, and there's no point offering another.
      setAttached(null);
      setDocumentsUnavailable(true);
    }
    setDocumentError(documentProblem(result));
  }

  function removeDocument() {
    setAttached(null);
    setDocumentError(null);
  }

  async function create(make: () => Promise<CreateResult>) {
    setBusy(true);
    setError(null);
    try {
      settle(await make());
    } catch {
      setError(copy.frame.notSaved);
    } finally {
      setBusy(false);
    }
  }

  async function draft() {
    const controller = new AbortController();
    drafts.current = controller;
    setDrafting(true);
    setError(null);
    setDocumentError(null);
    try {
      const document = documentsOffered ? attached : null;
      const result = await startFromDescription(describeDeps(), { description, name, purpose, kind, noun, document }, controller.signal);
      if (controller.signal.aborted) return;
      settle(result);
      // A draft that didn't become a dial may still have used one of the month's drafts.
      if (result.kind !== "created") void refreshAllowance().catch(() => {});
    } catch {
      if (!controller.signal.aborted) setError(copy.start.describe.failed);
    } finally {
      if (drafts.current === controller) drafts.current = null;
      if (!controller.signal.aborted) setDrafting(false);
    }
  }

  function stopDrafting() {
    drafts.current?.abort();
    setDrafting(false);
    void refreshAllowance().catch(() => {});
  }

  const meter = meterView(allowance, "definitions");
  const sheet = limit ? designLimitSheet(limit) : null;
  const card = describeCard(aiMeter(allowance));
  // Nothing else can be chosen or changed while a draft is being made or a document read.
  const locked = drafting || reading !== null;
  const choosing = busy || locked;

  const sharedFields = (
    <>
      <NameFields name={name} purpose={purpose} disabled={locked} onName={setName} onPurpose={setPurpose} />
      <KindChoice kind={kind} noun={words} disabled={locked} onKind={setKindChoice} />
      <NounFields
        singular={singular}
        plural={plural}
        disabled={locked}
        onSingular={(value) => {
          setSingular(value);
          if (!pluralTyped) setPlural(suggestPlural(value));
        }}
        onPlural={(value) => {
          setPlural(value);
          setPluralTyped(true);
        }}
      />
    </>
  );

  return (
    <section className="kd-builder-panel" aria-labelledby="kd-builder-question">
      <h1 id="kd-builder-question" className="kd-builder-question">
        {copy.start.question}
      </h1>
      {meter && <PlanMeter view={meter} template={copy.planMeter.designs} />}

      <h2 className="kd-builder-subheading">{copy.start.startingQuestion}</h2>
      <div className="kd-choice-grid">
        <ChoiceCard
          pressed={start === "describe"}
          title={copy.start.describe.title}
          body={documentsOffered ? copy.start.describe.body : copy.start.describe.bodyNoDocument}
          meta={card.meta ?? undefined}
          disabled={card.disabled || choosing}
          onClick={() => setStart("describe")}
        />
        <ChoiceCard pressed={start === "template"} title={copy.start.template.title} body={copy.start.template.body} disabled={choosing} onClick={() => setStart("template")} />
        <ChoiceCard
          pressed={start === "blank"}
          title={copy.start.blank.title}
          body={fill(copy.start.blank.body, nounValues(words))}
          disabled={choosing}
          onClick={() => setStart("blank")}
        />
      </div>

      {/* Arriving with Describe it chosen on a plan whose AI drafts are locked or used up shows the choice, not the form. */}
      {start === "describe" && allowance !== null && !card.disabled && (
        <DescribeStart
          description={description}
          onDescription={setDescription}
          drafting={drafting}
          upload={
            documentsOffered
              ? {
                  attached,
                  reading,
                  error: documentError,
                  canAdd: !documentsUnavailable,
                  onPick: (file) => void readFile(file),
                  onRemove: removeDocument,
                }
              : undefined
          }
          onDraft={() => void draft()}
          onStop={stopDrafting}
        >
          {sharedFields}
        </DescribeStart>
      )}

      {start === "template" && (
        <TemplatePicker noun={words} busy={busy} onPick={(template) => void create(() => startFromTemplate(builderDeps(), template.id))} />
      )}

      {start === "blank" && (
        <>
          {sharedFields}
          <div className="kd-builder-footer">
            <button
              type="button"
              className="kd-btn-primary"
              disabled={busy}
              onClick={() => void create(() => startBlank(builderDeps(), { name, purpose, kind, noun }))}
            >
              {busy ? copy.start.creating : copy.frame.continue}
            </button>
          </div>
        </>
      )}

      {error && (
        <p className="kd-builder-error" role="alert">
          {error}
        </p>
      )}
      {sheet && <LimitSheet view={sheet} onClose={onLeave} />}
    </section>
  );
}

function EditDial({ session, snapshot, onNext }: { session: DraftSession; snapshot: DraftSnapshot; onNext: () => void }) {
  const model = snapshot.model;
  const locked = snapshot.lockedLifecycle !== null;
  const kind: Kind = model.kind === "Simple" ? "Simple" : "Itemised";
  const words = nounFor(snapshot.noun);
  const [singular, setSingular] = useState(snapshot.noun?.singular ?? "");
  const [plural, setPlural] = useState(snapshot.noun?.plural ?? "");
  const [confirmSimple, setConfirmSimple] = useState(false);
  // Going to one colour per action point removes every item and every field asked of each item, so it asks first when there are
  // any. A checklist dial with no items yet can still have item fields, and losing those silently was a bug.
  const simpleQuestion = kind === "Itemised" ? switchToSimpleQuestion(model, words) : null;

  function chooseKind(next: Kind) {
    if (next === kind) return;
    if (next === "Simple" && simpleQuestion !== null) {
      setConfirmSimple(true);
      return;
    }
    session.edit((m) => setKind(m, next).model);
  }

  function changeNoun(one: string, many: string) {
    setSingular(one);
    setPlural(many);
    // The server takes both forms or neither, so half a word waits until the other half is typed.
    if (Boolean(one.trim()) === Boolean(many.trim())) session.setNoun({ singular: one.trim(), plural: many.trim() });
  }

  return (
    <section className="kd-builder-panel" aria-labelledby="kd-builder-question">
      <h1 id="kd-builder-question" className="kd-builder-question">
        {copy.start.question}
      </h1>
      <NameFields
        name={model.name}
        purpose={model.description ?? ""}
        disabled={locked}
        onName={(value) => session.edit((m) => ({ ...m, name: value }))}
        onPurpose={(value) => session.edit((m) => ({ ...m, description: value || undefined }))}
      />
      <KindChoice kind={kind} noun={words} disabled={locked} onKind={chooseKind} />
      {confirmSimple && simpleQuestion && (
        <div className="kd-builder-confirm" role="alertdialog" aria-labelledby="kd-builder-confirm-simple">
          <p id="kd-builder-confirm-simple">{simpleQuestion}</p>
          <div className="kd-builder-actions">
            <button type="button" className="kd-btn" onClick={() => setConfirmSimple(false)}>
              {copy.start.keepChecklists}
            </button>
            <button
              type="button"
              className="kd-btn-primary"
              onClick={() => {
                session.edit((m) => setKind(m, "Simple").model);
                setConfirmSimple(false);
              }}
            >
              {copy.start.switchConfirm}
            </button>
          </div>
        </div>
      )}
      <NounFields
        singular={singular}
        plural={plural}
        disabled={locked}
        onSingular={(value) => changeNoun(value, plural)}
        onPlural={(value) => changeNoun(singular, value)}
      />
      <div className="kd-builder-footer">
        <button type="button" className="kd-btn-primary" onClick={onNext}>
          {copy.frame.continue}
        </button>
      </div>
    </section>
  );
}

function ChoiceCard({ pressed, title, body, meta, disabled, onClick }: { pressed: boolean; title: string; body: string; meta?: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" className="kd-choice" aria-pressed={pressed} disabled={disabled} onClick={onClick}>
      <span className="kd-choice-title">{title}</span>
      <span className="kd-choice-body">{body}</span>
      {meta && <span className="kd-choice-meta">{meta}</span>}
    </button>
  );
}

function NameFields({
  name,
  purpose,
  disabled,
  onName,
  onPurpose,
}: {
  name: string;
  purpose: string;
  disabled?: boolean;
  onName: (value: string) => void;
  onPurpose: (value: string) => void;
}) {
  return (
    <div className="kd-builder-fields">
      <label className="kd-builder-field">
        <span>{copy.start.nameLabel}</span>
        <input value={name} disabled={disabled} placeholder={copy.start.namePlaceholder} onChange={(e) => onName(e.target.value)} />
      </label>
      <label className="kd-builder-field">
        <span>{copy.start.purposeLabel}</span>
        <input value={purpose} disabled={disabled} placeholder={copy.start.purposePlaceholder} onChange={(e) => onPurpose(e.target.value)} />
      </label>
      <p className="kd-builder-muted">{copy.start.bothChangeable}</p>
    </div>
  );
}

function KindChoice({ kind, noun, disabled, onKind }: { kind: Kind; noun: SegmentNoun; disabled?: boolean; onKind: (kind: Kind) => void }) {
  const words = nounValues(noun);
  return (
    <>
      <h2 className="kd-builder-subheading">{fill(copy.start.forkQuestion, words)}</h2>
      <div className="kd-choice-grid">
        <ChoiceCard
          pressed={kind === "Simple"}
          disabled={disabled}
          title={fill(copy.start.oneColour.title, words)}
          body={copy.start.oneColour.body}
          meta={`${copy.start.recommended} · ${copy.start.oneColour.goodFor}`}
          onClick={() => onKind("Simple")}
        />
        <ChoiceCard
          pressed={kind === "Itemised"}
          disabled={disabled}
          title={fill(copy.start.checklist.title, words)}
          body={copy.start.checklist.body}
          onClick={() => onKind("Itemised")}
        />
      </div>
      <p className="kd-builder-muted">{copy.start.forkNote}</p>
    </>
  );
}

function NounFields({
  singular,
  plural,
  disabled,
  onSingular,
  onPlural,
}: {
  singular: string;
  plural: string;
  disabled?: boolean;
  onSingular: (value: string) => void;
  onPlural: (value: string) => void;
}) {
  return (
    <>
      <h2 className="kd-builder-subheading">{copy.start.nounQuestion}</h2>
      <div className="kd-noun-fields">
        <label className="kd-builder-field">
          <span>{copy.start.nounOne}</span>
          <input value={singular} disabled={disabled} placeholder="check" onChange={(e) => onSingular(e.target.value)} />
        </label>
        <label className="kd-builder-field">
          <span>{copy.start.nounMany}</span>
          <input value={plural} disabled={disabled} placeholder="checks" onChange={(e) => onPlural(e.target.value)} />
        </label>
      </div>
      <p className="kd-builder-muted">{copy.start.nounHint}</p>
    </>
  );
}
